/** @jsx jsx */

/** @jsxFrag */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client/react';

import { Dialog } from '@coursera/cds-core';
import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import AddRecoveryEmailStep from 'bundles/program-home/components/modals/AddRecoveryEmailStep';
import ConfirmRecoveryEmailStep from 'bundles/program-home/components/modals/ConfirmRecoveryEmailStep';
import { STEPS } from 'bundles/program-home/constants/forcedSSORecoveryEmail';
import useAlternativeEmailModal from 'bundles/program-home/queryHooks/useAlternativeEmailModal';
import type {
  InsertUserEmailMutationResult,
  InsertUserEmailMutationVariables,
} from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import { InsertUserEmailMutation } from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import { getUnverifiedNonPrimaryEmail } from 'bundles/saml-sso/shared/utils/email';
import { useUserEmails } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

import _t from 'i18n!nls/program-home';

type WrapperProps = {
  userId: number;
  programId: string;
};

type Props = {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  onDefer: () => void;
  userEmails: UserEmail[];
  onUpdate: () => void;
};

export const ForcedSSORecoveryEmailModal = ({ isOpen, userId, userEmails, onUpdate, onClose, onDefer }: Props) => {
  const unverifiedNonPrimaryEmail = getUnverifiedNonPrimaryEmail(userEmails);
  const [emailField, setEmailField] = useState<string>(unverifiedNonPrimaryEmail?.emailAddress ?? '');
  const [emailFieldError, setEmailFieldError] = useState<string>('');

  const [currentStep, setCurrentStep] = useState<keyof typeof STEPS>(
    unverifiedNonPrimaryEmail ? STEPS.CONFIRM_ADD : STEPS.ADD_EMAIL
  );

  React.useEffect(() => {
    if (unverifiedNonPrimaryEmail?.emailAddress && isOpen) {
      setCurrentStep(STEPS.CONFIRM_ADD);
      setEmailField(unverifiedNonPrimaryEmail.emailAddress);
    }
  }, [unverifiedNonPrimaryEmail?.emailAddress, isOpen]);

  const onEmailUpdate = () => {
    setEmailFieldError('');
    setCurrentStep(STEPS.CONFIRM_ADD);
    onUpdate();
  };

  const [insertUserEmail, { loading }] = useMutation<InsertUserEmailMutationResult, InsertUserEmailMutationVariables>(
    InsertUserEmailMutation,
    {
      onError: ({ networkError }) => {
        // @ts-expect-error(apollo-v3): TS2345: Argument of type 'readonly GraphQLError[] | undefined' is not assignable
        if (networkError?.response?.status === 409) {
          setEmailFieldError(_t('This email is being used with another account! Try using a different email address'));
        } else {
          setEmailFieldError(_t('There was an error while adding the email, please try again'));
        }
      },
      onCompleted: (data) => {
        const addedEmail = data?.InsertUserEmailResponse?.userEmail?.emailAddress;
        if (addedEmail) {
          onEmailUpdate();
        }
      },
    }
  );

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailField(value);
    setEmailFieldError('');
  };

  const getValidationErrors = () => {
    const errors = runValidations(emailField, [required, email]);
    return errors;
  };

  const onSubmitEmail = () => {
    const errors = getValidationErrors();

    if (errors) {
      setEmailFieldError(errors);
      return;
    }

    insertUserEmail({
      variables: {
        input: { emailAddress: emailField, isPrimary: false, userId },
      },
    });
  };

  const getModalContent = () => {
    if (currentStep === STEPS.ADD_EMAIL) {
      return (
        <AddRecoveryEmailStep
          emailField={emailField}
          onChangeEmail={onChangeEmail}
          getValidationErrors={getValidationErrors}
          onSubmitEmail={onSubmitEmail}
          emailFieldError={emailFieldError}
          loading={loading}
          onDefer={onDefer}
        />
      );
    }

    return (
      <ConfirmRecoveryEmailStep
        userId={userId}
        emailField={emailField}
        onUpdateEmailClick={() => setCurrentStep(STEPS.ADD_EMAIL)}
        onDefer={onDefer}
      />
    );
  };

  return (
    <Dialog aria-describedby="description" aria-labelledby="title" open={isOpen} variant="standard" onClose={onClose}>
      {getModalContent()}
    </Dialog>
  );
};

export const ForcedSSORecoveryEmailModalWrapper = ({ userId, programId }: WrapperProps) => {
  const { userEmails = [], refetchUserEmails } = useUserEmails();
  const { isOpen, onClose, onDefer } = useAlternativeEmailModal(userId, userEmails, programId);

  const onUpdate = () => {
    refetchUserEmails?.();
  };

  return (
    <ForcedSSORecoveryEmailModal
      userId={userId}
      isOpen={isOpen}
      onClose={onClose}
      onDefer={onDefer}
      userEmails={userEmails}
      onUpdate={onUpdate}
    />
  );
};

export default ForcedSSORecoveryEmailModalWrapper;
